import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { ArrowLeftRight, Layers, MoreVertical, Users } from "lucide-react";
import { color, radius, space, motion as motionTokens } from "@tokens/index";
import { IconButton, Menu, MenuItem, Popover, Text, TextInput } from "../../primitives";
import { buildQueueNameResolver, formatAncestorPath, type QueueNameResolver, type RecipientSummary } from "../workflowRecipients";
import type { QueueSummary, QueueTreeResponse } from "../../../experience-shell/queues/types";
import type { WorkItem } from "../../../experience-shell/workflows";
import { filterQueueCandidates } from "./queueCandidates";
import { hairline } from "./expandMotion";

/**
 * Queue chip with a two-step control: a `Menu` with one action, then a
 * `Popover` list with a search input. Renders "Unassigned" when no queue
 * resolves.
 */
export function TaskQueueAssignment({
  task,
  assignedRecipients,
  queueTree,
  onReassign,
  isReassigning,
}: {
  readonly task: WorkItem;
  readonly assignedRecipients: RecipientSummary | null;
  readonly queueTree: QueueTreeResponse | undefined;
  readonly onReassign:
    | ((workItemId: string, currentQueueIds: readonly string[], newQueueId: string) => void)
    | undefined;
  readonly isReassigning: boolean;
}): ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setFilter("");
  }, []);

  useEffect(() => {
    if (isReassigning) {
      setMenuOpen(false);
      closePicker();
    }
  }, [isReassigning, closePicker]);

  const candidates = useMemo(() => filterQueueCandidates(queueTree, filter), [queueTree, filter]);
  const resolveQueueName = useMemo(() => buildQueueNameResolver(queueTree), [queueTree]);

  const handlePick = useCallback(
    (queue: QueueSummary) => {
      onReassign?.(task.id, task.visibleToQueueIds, queue.id);
    },
    [onReassign, task.id, task.visibleToQueueIds],
  );

  const isUnassigned = assignedRecipients === null;
  const hasReassign = onReassign !== undefined;
  const tone = isUnassigned ? color.fg.muted : color.accent.primary;

  return (
    <span
      title={isUnassigned ? "Unassigned" : assignedRecipients.names.join(", ")}
      aria-label={isUnassigned ? "Unassigned" : `Assigned to ${assignedRecipients.names.join(", ")}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: space[1],
        paddingLeft: space[2],
        paddingRight: hasReassign ? 0 : space[2],
        minHeight: hasReassign ? 32 : 24,
        borderRadius: radius.pill,
        background: color.bg.surface2,
        border: hairline,
        color: tone,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Layers size={11} strokeWidth={2.25} aria-hidden="true" />
      <Text role="caption" size="xs" weight="medium" truncate style={{ color: "inherit" }}>
        {isUnassigned ? "Unassigned" : `Assigned to ${assignedRecipients.label}`}
      </Text>
      {hasReassign && (
        <>
          <IconButton
            ref={triggerRef}
            icon={MoreVertical}
            size="sm"
            variant="ghost"
            aria-label="Queue actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen || pickerOpen}
            tooltip="Queue actions"
            onClick={(e) => {
              e.stopPropagation();
              if (menuOpen || pickerOpen) {
                setMenuOpen(false);
                closePicker();
              } else {
                setMenuOpen(true);
              }
            }}
          />
          <Menu open={menuOpen} onClose={() => setMenuOpen(false)} anchorRef={triggerRef} minWidth={200} sheetOnCompact>
            <MenuItem
              label={isUnassigned ? "Assign to queue" : "Reassign to queue"}
              icon={<ArrowLeftRight size={14} strokeWidth={1.75} aria-hidden="true" />}
              onSelect={() => {
                setFilter("");
                setPickerOpen(true);
              }}
            />
          </Menu>
          <Popover
            open={pickerOpen}
            onClose={closePicker}
            anchorRef={triggerRef}
            role="listbox"
            minWidth={280}
            initialFocus={searchRef}
            sheetOnCompact
          >
            <div style={{ display: "flex", flexDirection: "column", gap: space[1], padding: space[1] }}>
              <TextInput
                ref={searchRef}
                type="text"
                fullWidth
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search queues…"
                aria-label="Search queues"
              />
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {candidates.length === 0 ? (
                  <div style={{ padding: `${space[3]} ${space[3]}` }}>
                    <Text role="caption" tone="subtle">
                      {queueTree === undefined
                        ? "Loading queues…"
                        : filter.trim().length > 0
                          ? "No matching queues"
                          : "No workable queues"}
                    </Text>
                  </div>
                ) : (
                  candidates.map((q) => (
                    <QueueOption
                      key={q.id}
                      queue={q}
                      disabled={isReassigning}
                      onPick={handlePick}
                      resolveQueueName={resolveQueueName}
                    />
                  ))
                )}
              </div>
            </div>
          </Popover>
        </>
      )}
    </span>
  );
}

function QueueOption({
  queue,
  disabled,
  onPick,
  resolveQueueName,
}: {
  readonly queue: QueueSummary;
  readonly disabled: boolean;
  readonly onPick: (queue: QueueSummary) => void;
  readonly resolveQueueName: QueueNameResolver;
}): ReactElement {
  const [hovered, setHovered] = useState(false);
  const path = formatAncestorPath(queue.ancestorPath, resolveQueueName);
  return (
    <button
      type="button"
      role="option"
      aria-selected={false}
      disabled={disabled}
      onClick={() => onPick(queue)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        appearance: "none",
        width: "100%",
        minHeight: 40,
        display: "flex",
        alignItems: "center",
        gap: space[2],
        padding: `${space[2]} ${space[3]}`,
        background: hovered && !disabled ? color.bg.hover : "transparent",
        border: "none",
        borderRadius: radius.sm,
        cursor: disabled ? "wait" : "pointer",
        color: color.fg.default,
        textAlign: "left",
        transition: `background ${motionTokens.durations.fast} ${motionTokens.easings.standard}`,
      }}
    >
      <Users size={14} strokeWidth={2} style={{ color: color.accent.primary, flexShrink: 0 }} aria-hidden="true" />
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Text role="bodySm" weight="medium" truncate>
          {queue.name}
        </Text>
        {path !== null && (
          <Text role="caption" tone="subtle" truncate>
            {path}
          </Text>
        )}
      </div>
    </button>
  );
}
